import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  OndemandVideo as VideoIcon
} from '@mui/icons-material';

interface TrainingVideo {
  id: string;
  title: string;
  description: string;
  duration: string;
  thumbnail: string;
  videoUrl: string;
  category: string;
  tags: string[];
}

const mockVideos: TrainingVideo[] = [
  {
    id: '1',
    title: 'Introduction to Attendance Tracking',
    description: 'Learn the basics of tracking and managing student attendance using the system.',
    duration: '10:30',
    thumbnail: 'http://img.youtube.com/vi/kfyu_NdlZm4/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/kfyu_NdlZm4',
    category: 'Basic Training',
    tags: ['attendance', 'beginner']
  },
  {
    id: '2',
    title: 'Advanced Intervention Techniques',
    description: 'Deep dive into creating and managing effective intervention strategies.',
    duration: '15:45',
    thumbnail: 'http://img.youtube.com/vi/ra_Lq0DbkiM/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/ra_Lq0DbkiM',
    category: 'Advanced Training',
    tags: ['intervention', 'advanced']
  },
  {
    id: '3',
    title: 'Working with Student Data',
    description: 'Best practices for managing and analyzing student information.',
    duration: '12:20',
    thumbnail: 'http://img.youtube.com/vi/URAZHxrsik8/maxresdefault.jpg',
    videoUrl: 'https://www.youtube.com/embed/URAZHxrsik8',
    category: 'Data Management',
    tags: ['data', 'analysis']
  }
];

export const TrainingVideos: React.FC = () => {
  const [selectedVideo, setSelectedVideo] = useState<TrainingVideo | null>(null);

  const handleOpenVideo = (video: TrainingVideo) => {
    setSelectedVideo(video);
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <VideoIcon sx={{ mr: 1 }} />
        <Typography variant="h6">
          Training Videos
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {mockVideos.map((video) => (
          <Grid item xs={12} md={4} key={video.id}>
            <Card>
              <CardMedia
                component="img"
                height="140"
                image={video.thumbnail}
                alt={video.title}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {video.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {video.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {video.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Duration: {video.duration}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  startIcon={<PlayIcon />}
                  onClick={() => handleOpenVideo(video)}
                  fullWidth
                >
                  Watch Video
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={Boolean(selectedVideo)}
        onClose={handleCloseVideo}
        maxWidth="md"
        fullWidth
      >
        {selectedVideo && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {selectedVideo.title}
                <IconButton onClick={handleCloseVideo} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                <Box
                  component="iframe"
                  src={selectedVideo.videoUrl}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allowFullScreen
                />
              </Box>
              <Typography variant="body1" sx={{ mt: 2 }}>
                {selectedVideo.description}
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Paper>
  );
};